import { useState } from "react";
import { ArrowRight, CheckCircle2, ChevronDown, Loader2, MessageCircle, Upload } from "lucide-react";
import { Eyebrow, Reveal, WHATSAPP_URL } from "../landing/shared";

const INDUSTRIES = [
    "Healthcare",
    "Construction",
    "Engineering",
    "Hospitality",
    "Manufacturing",
    "Logistics",
    "Agriculture",
    "Oil & Gas",
    "Technology",
    "Security",
    "Skilled Trades",
    "Professional Services",
];

const EXPERIENCE_OPTIONS = ["0–1 years", "1–3 years", "3–5 years", "5–10 years", "10+ years"];
const YES_NO_OPTIONS = ["Yes", "No", "To be discussed"];

const inputClass =
    "w-full border border-ink/15 bg-ivory px-4 py-3.5 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-forest";

const Field = ({ label, htmlFor, optional, required, children }) => (
    <div>
        <label
            htmlFor={htmlFor}
            className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-ink/55"
        >
            {label}
            {required && (
                <span className="ml-1 text-champagne" aria-hidden="true">
                    *
                </span>
            )}
            {optional && <span className="ml-2 normal-case tracking-normal text-ink/40">(optional)</span>}
        </label>
        {children}
    </div>
);

const SelectWrap = ({ children }) => (
    <div className="relative">
        {children}
        <ChevronDown
            size={16}
            strokeWidth={1.5}
            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-ink/40"
        />
    </div>
);

const SectionLabel = ({ children }) => (
    <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-forest">
        <span className="h-px w-6 bg-champagne" aria-hidden="true" />
        {children}
    </p>
);

export const RequirementForm = () => {
    const [status, setStatus] = useState("idle"); // idle | submitting | success | error
    const [fileName, setFileName] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (status === "submitting") return;
        setStatus("submitting");
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/recruitment-requests`,
                { method: "POST", body: new FormData(e.target) }
            );
            if (!res.ok) throw new Error("send failed");
            setStatus("success");
            document.getElementById("employer-form")?.scrollIntoView({ behavior: "smooth" });
        } catch {
            setStatus("error");
        }
    };

    const submitAnother = () => {
        setFileName("");
        setStatus("idle");
    };

    return (
        <section id="employer-form" data-testid="employer-form-section" className="bg-ivory py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <Reveal className="max-w-2xl">
                    <Eyebrow>Recruitment Request</Eyebrow>
                    <h2 className="mt-5 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl lg:text-5xl">
                        Tell Us What You Need
                    </h2>
                    <p className="mt-6 text-base leading-relaxed text-ink/65">
                        Share your workforce requirements and our recruitment team can review the
                        opportunity. Fields marked <span className="text-champagne">*</span> are required.
                    </p>
                </Reveal>

                <Reveal delay={0.1} className="mt-12">
                    <div className="border border-ink/10 bg-sand p-7 sm:p-10 lg:p-14">
                        {status === "success" ? (
                            <div
                                data-testid="er-form-success"
                                className="flex min-h-96 flex-col items-center justify-center text-center"
                            >
                                <CheckCircle2 size={44} strokeWidth={1.25} className="text-forest" />
                                <h3 className="mt-6 max-w-lg font-serif text-2xl font-medium tracking-tight text-ink">
                                    Thank you. Your recruitment request has been received.
                                </h3>
                                <p className="mt-3 max-w-md text-sm leading-relaxed text-ink/60">
                                    Our team will review your requirements and contact you using the
                                    details provided.
                                </p>
                                <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
                                    <button
                                        type="button"
                                        data-testid="er-submit-another-btn"
                                        onClick={submitAnother}
                                        className="inline-flex items-center gap-2 bg-forest px-7 py-3.5 text-sm font-medium text-ivory transition-colors hover:bg-forest-deep"
                                    >
                                        Submit Another Request
                                    </button>
                                    <a
                                        href={WHATSAPP_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="er-success-whatsapp-btn"
                                        className="inline-flex items-center gap-2 border border-forest/30 px-7 py-3.5 text-sm font-medium text-forest transition-colors hover:bg-forest hover:text-ivory"
                                    >
                                        <MessageCircle size={16} strokeWidth={1.5} />
                                        WhatsApp Us
                                    </a>
                                </div>
                            </div>
                        ) : (
                            <form data-testid="employer-requirement-form" onSubmit={handleSubmit}>
                                <input
                                    type="text"
                                    name="fax"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    aria-hidden="true"
                                    className="absolute -left-[9999px] h-0 w-0 opacity-0"
                                />
                                <div className="space-y-12">
                                    <fieldset className="min-w-0 space-y-6" disabled={status === "submitting"}>
                                        <SectionLabel>A — Company</SectionLabel>
                                        <div className="grid gap-6 sm:grid-cols-2">
                                            <Field label="Company Name" htmlFor="er-company-name" required>
                                                <input id="er-company-name" name="company_name" data-testid="er-company-name-input" type="text" required placeholder="Your organization" className={inputClass} />
                                            </Field>
                                            <Field label="Country" htmlFor="er-country" required>
                                                <input id="er-country" name="country" data-testid="er-country-input" type="text" required placeholder="Country of operation" className={inputClass} />
                                            </Field>
                                            <Field label="Industry" htmlFor="er-industry" required>
                                                <SelectWrap>
                                                    <select id="er-industry" name="industry" data-testid="er-industry-select" required defaultValue="" className={`${inputClass} cursor-pointer appearance-none pr-10`}>
                                                        <option value="" disabled>Select an industry</option>
                                                        {INDUSTRIES.map((industry) => (
                                                            <option key={industry} value={industry}>{industry}</option>
                                                        ))}
                                                    </select>
                                                </SelectWrap>
                                            </Field>
                                            <Field label="Company Website" htmlFor="er-website" optional>
                                                <input id="er-website" name="website" data-testid="er-website-input" type="url" placeholder="https://" className={inputClass} />
                                            </Field>
                                        </div>
                                    </fieldset>

                                    <fieldset className="min-w-0 space-y-6 border-t border-ink/10 pt-12" disabled={status === "submitting"}>
                                        <SectionLabel>B — Contact</SectionLabel>
                                        <div className="grid gap-6 sm:grid-cols-2">
                                            <Field label="Contact Person" htmlFor="er-contact-person" required>
                                                <input id="er-contact-person" name="contact_person" data-testid="er-contact-person-input" type="text" required placeholder="Full name" className={inputClass} />
                                            </Field>
                                            <Field label="Job Title" htmlFor="er-job-title" optional>
                                                <input id="er-job-title" name="job_title" data-testid="er-job-title-input" type="text" placeholder="e.g. HR Director" className={inputClass} />
                                            </Field>
                                            <Field label="Email Address" htmlFor="er-email" required>
                                                <input id="er-email" name="email" data-testid="er-email-input" type="email" required placeholder="you@company.com" className={inputClass} />
                                            </Field>
                                            <Field label="Phone / WhatsApp" htmlFor="er-phone" required>
                                                <input id="er-phone" name="phone" data-testid="er-phone-input" type="tel" required placeholder="+ ..." className={inputClass} />
                                            </Field>
                                        </div>
                                    </fieldset>

                                    <fieldset className="min-w-0 space-y-6 border-t border-ink/10 pt-12" disabled={status === "submitting"}>
                                        <SectionLabel>C — Workforce Requirement</SectionLabel>
                                        <div className="grid gap-6 sm:grid-cols-2">
                                            <Field label="Position / Job Title" htmlFor="er-position" required>
                                                <input id="er-position" name="position" data-testid="er-position-input" type="text" required placeholder="e.g. Registered Nurses" className={inputClass} />
                                            </Field>
                                            <Field label="Number of Workers Required" htmlFor="er-workers" required>
                                                <input id="er-workers" name="workers" data-testid="er-workers-input" type="number" min="1" required placeholder="e.g. 25" className={inputClass} />
                                            </Field>
                                            <Field label="Required Qualifications" htmlFor="er-qualifications" optional>
                                                <input id="er-qualifications" name="qualifications" data-testid="er-qualifications-input" type="text" placeholder="Degrees, certifications, licences…" className={inputClass} />
                                            </Field>
                                            <Field label="Years of Experience" htmlFor="er-experience" optional>
                                                <SelectWrap>
                                                    <select id="er-experience" name="experience" data-testid="er-experience-select" defaultValue="" className={`${inputClass} cursor-pointer appearance-none pr-10`}>
                                                        <option value="" disabled>Select experience</option>
                                                        {EXPERIENCE_OPTIONS.map((option) => (
                                                            <option key={option} value={option}>{option}</option>
                                                        ))}
                                                    </select>
                                                </SelectWrap>
                                            </Field>
                                            <Field label="Expected Start Date" htmlFor="er-start-date" optional>
                                                <input id="er-start-date" name="start_date" data-testid="er-start-date-input" type="date" className={inputClass} />
                                            </Field>
                                            <Field label="Salary / Compensation Range" htmlFor="er-salary" optional>
                                                <input id="er-salary" name="salary" data-testid="er-salary-input" type="text" placeholder="e.g. $2,000–$2,800 / month" className={inputClass} />
                                            </Field>
                                        </div>
                                    </fieldset>

                                    <fieldset className="min-w-0 space-y-6 border-t border-ink/10 pt-12" disabled={status === "submitting"}>
                                        <SectionLabel>D — Additional Requirements</SectionLabel>
                                        <div className="grid gap-6 sm:grid-cols-2">
                                            <Field label="Accommodation Provided?" htmlFor="er-accommodation" optional>
                                                <SelectWrap>
                                                    <select id="er-accommodation" name="accommodation" data-testid="er-accommodation-select" defaultValue="" className={`${inputClass} cursor-pointer appearance-none pr-10`}>
                                                        <option value="" disabled>Select an option</option>
                                                        {YES_NO_OPTIONS.map((option) => (
                                                            <option key={option} value={option}>{option}</option>
                                                        ))}
                                                    </select>
                                                </SelectWrap>
                                            </Field>
                                            <Field label="Transportation Provided?" htmlFor="er-transportation" optional>
                                                <SelectWrap>
                                                    <select id="er-transportation" name="transportation" data-testid="er-transportation-select" defaultValue="" className={`${inputClass} cursor-pointer appearance-none pr-10`}>
                                                        <option value="" disabled>Select an option</option>
                                                        {YES_NO_OPTIONS.map((option) => (
                                                            <option key={option} value={option}>{option}</option>
                                                        ))}
                                                    </select>
                                                </SelectWrap>
                                            </Field>
                                            <Field label="Preferred Talent Market" htmlFor="er-talent-market" optional>
                                                <input id="er-talent-market" name="talent_market" data-testid="er-talent-market-input" type="text" placeholder="e.g. Nigeria, West Africa" className={inputClass} />
                                            </Field>
                                            <div>
                                                <span className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-ink/55">
                                                    Upload Job Description
                                                    <span className="ml-2 normal-case tracking-normal text-ink/40">(optional)</span>
                                                </span>
                                                <label
                                                    htmlFor="er-jd-file"
                                                    className="flex cursor-pointer items-center justify-between gap-4 border border-dashed border-ink/25 bg-ivory px-4 py-4 text-sm transition-colors hover:border-forest"
                                                >
                                                    <span className={`truncate ${fileName ? "font-medium text-ink" : "text-ink/45"}`}>
                                                        {fileName || "Choose a file to attach"}
                                                    </span>
                                                    <span className="inline-flex shrink-0 items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-forest">
                                                        <Upload size={15} strokeWidth={1.5} />
                                                        Browse
                                                    </span>
                                                </label>
                                                <p className="mt-2 text-xs text-ink/40">PDF, DOC or DOCX — up to 5 MB</p>
                                                <input
                                                    id="er-jd-file"
                                                    name="job_description"
                                                    data-testid="er-jd-file-input"
                                                    type="file"
                                                    accept=".pdf,.doc,.docx"
                                                    className="hidden"
                                                    onChange={(e) => setFileName(e.target.files?.[0]?.name || "")}
                                                />
                                            </div>
                                            <div className="sm:col-span-2">
                                                <Field label="Additional Requirements" htmlFor="er-additional" optional>
                                                    <textarea id="er-additional" name="additional" data-testid="er-additional-input" rows={4} placeholder="Anything else we should know about this requirement…" className={`${inputClass} resize-none`} />
                                                </Field>
                                            </div>
                                        </div>
                                    </fieldset>
                                </div>

                                {status === "error" && (
                                    <div
                                        data-testid="er-form-error"
                                        className="mt-10 border border-ink/20 bg-ivory p-5 text-sm leading-relaxed text-ink/70"
                                    >
                                        Your request could not be sent right now. Please try again, or
                                        contact us directly on{" "}
                                        <a
                                            href={WHATSAPP_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-medium text-forest underline underline-offset-4"
                                        >
                                            WhatsApp
                                        </a>{" "}
                                        or by{" "}
                                        <a
                                            href="mailto:contact@afrinexaglobal.org"
                                            className="font-medium text-forest underline underline-offset-4"
                                        >
                                            email
                                        </a>
                                        .
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    data-testid="er-submit-button"
                                    disabled={status === "submitting"}
                                    className="mt-12 inline-flex w-full items-center justify-center gap-2 bg-forest px-10 py-5 text-sm font-semibold uppercase tracking-[0.12em] text-ivory transition-colors duration-300 hover:bg-forest-deep disabled:cursor-not-allowed disabled:opacity-80 sm:w-auto"
                                >
                                    {status === "submitting" ? (
                                        <>
                                            <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />
                                            Submitting…
                                        </>
                                    ) : (
                                        <>
                                            Submit Recruitment Request
                                            <ArrowRight size={16} strokeWidth={1.5} />
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
